'use client'

import Calendar from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/Calendar'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import { getFormattedDate } from '@/src/utils/date'
import { Box, Button, Menu } from '@mui/material'
import { useLocale, useTranslations } from 'next-intl'
import { useCallback, useRef } from 'react'

export type HotelDateFieldType = 'checkIn' | 'checkOut'

interface HotelDateFieldProps {
    fieldType: HotelDateFieldType
    value: Date | null
    checkIn: Date | null
    minDate?: Date
    onChange: (date: Date | null) => void
    active: boolean
    onActivate: () => void
    onClose: () => void
}

export const HotelDateField: React.FC<HotelDateFieldProps> = ({
    fieldType,
    value,
    checkIn,
    minDate,
    onChange,
    active,
    onActivate,
    onClose
}) => {
    const locale = useLocale()
    const t = useTranslations('common')
    const { formatDate } = useFormatDate()
    const buttonRef = useRef<HTMLButtonElement>(null)

    const isCheckIn = fieldType === 'checkIn'
    const label = isCheckIn ? t('checkIn') : t('checkOut')

    const formatDateToDisplay = (date: Date | null) => {
        if (date) {
            return formatDate(getFormattedDate(date, 'MM/DD/YYYY'), 'ddd, DD MMM')
        }
        return t('selectDate')
    }

    const handleToggle = useCallback(() => {
        if (active) {
            onClose()
        } else {
            onActivate()
        }
    }, [active, onActivate, onClose])

    const handleCalendarClick = useCallback(
        (dates: { from?: Date; to?: Date }) => {
            if (isCheckIn && dates.from) {
                onChange(dates.from)
                onClose()
                return
            }
            if (!isCheckIn && dates.to) {
                onChange(dates.to)
                onClose()
            }
        },
        [isCheckIn, onChange, onClose]
    )

    const selectedDates = isCheckIn
        ? { from: value, to: null }
        : { from: checkIn, to: value }

    const calendarMinDate = isCheckIn ? new Date() : minDate ?? (checkIn ? checkIn : new Date())

    return (
        <Box sx={{ width: '100%' }}>
            <Button
                ref={buttonRef}
                onClick={handleToggle}
                sx={{
                    textTransform: 'none',
                    p: 0,
                    width: '100%',
                    justifyContent: 'flex-start',
                    '&:hover': { backgroundColor: 'transparent' },
                    fontWeight: active ? 600 : 400,
                    color: active ? 'primary.main' : 'inherit',
                    backgroundColor: active ? 'rgba(25, 118, 210, 0.08)' : 'transparent'
                }}
            >
                <Box sx={{ textAlign: 'left' }}>
                    <Box component="span" sx={{ display: 'block', fontSize: '0.75rem', color: 'grey.600' }}>
                        {label}
                    </Box>
                    <Box component="span" sx={{ fontSize: '0.875rem' }}>
                        {formatDateToDisplay(value)}
                    </Box>
                </Box>
            </Button>

            <Menu
                anchorEl={buttonRef.current}
                onClose={onClose}
                open={active && !!buttonRef.current}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                disablePortal={false}
                sx={{ top: 15 }}
            >
                <Calendar
                    isRtl={locale === 'ar'}
                    className="w-100p"
                    title=""
                    selectedDates={selectedDates}
                    roundTrip={!isCheckIn}
                    onClickCalendar={handleCalendarClick}
                    fareCalendar={{}}
                    cheapestPricesByMonth={{}}
                    directionClick={() => {}}
                    activeOnward={isCheckIn}
                    activeReturn={!isCheckIn}
                    isMulticity={false}
                    multiCityDestinations={{}}
                    index={1}
                    minDate={calendarMinDate}
                />
            </Menu>
        </Box>
    )
}
